/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth, useTrpc } from "@app/vue/providers/index";

type Props = { from: string; to: string };
type TrackerRow = {
  payment_id: number;
  sale_date: string;
  method: string | null;
  card_brand: string | null;
  gross: number;
  fee: number;
  payout_reference: string | null;
  status: "paid" | "pending";
};

export const SettlementsTab = defineComponent<Props>({
  name: "SettlementsTab",
  props: ["from", "to"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();
    const currency = () => auth.user.value?.restaurant.currency || "CHF";
    const money = (value: number) =>
      formatPriceInLocale(value, true, 2, currency());

    const tracker = useQuery({
      queryKey: computed(() => [
        "accounting",
        "settlements",
        props.from,
        props.to,
      ]),
      queryFn: () =>
        trpc.accounting.settlementTracker.query({
          from: props.from.slice(0, 10),
          to: props.to.slice(0, 10),
          limit: 100,
        }),
    });
    const outstanding = useQuery({
      queryKey: ["accounting", "outstanding-receivables"],
      queryFn: () => trpc.accounting.outstandingReceivables.query(),
    });
    const aging = useQuery({
      queryKey: ["accounting", "settlement-aging"],
      queryFn: () => trpc.accounting.settlementAging.query({}),
    });

    const rows = computed(
      () => ((tracker.data.value as any)?.data ?? []) as TrackerRow[],
    );
    return () => (
      <div class="space-y-4">
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [
              t("reports.settlements.outstanding"),
              (outstanding.data.value as any)?.total ?? 0,
            ],
            [
              t("reports.settlements.age_0_7"),
              (aging.data.value as any)?.zero_to_seven ?? 0,
            ],
            [
              t("reports.settlements.age_8_14"),
              (aging.data.value as any)?.eight_to_fourteen ?? 0,
            ],
            [
              t("reports.settlements.age_over_14"),
              (aging.data.value as any)?.over_fourteen ?? 0,
            ],
          ].map(([label, value]) => (
            <div class="rounded-box border border-base-300 bg-base-100 p-4">
              <div class="text-xs text-base-content/60">{label}</div>
              <div class="mt-1 text-xl font-semibold">
                {money(Number(value))}
              </div>
            </div>
          ))}
        </div>
        <div class="overflow-x-auto rounded-box border border-base-300 bg-base-100">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>{t("reports.settlements.payment")}</th>
                <th>{t("reports.settlements.date")}</th>
                <th>{t("reports.settlements.method")}</th>
                <th>{t("reports.settlements.gross")}</th>
                <th>{t("reports.settlements.fee")}</th>
                <th>{t("reports.settlements.payout")}</th>
                <th>{t("reports.settlements.status")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.value.map((row) => (
                <tr key={row.payment_id}>
                  <td>#{row.payment_id}</td>
                  <td>{row.sale_date}</td>
                  <td>{row.card_brand || row.method || "—"}</td>
                  <td>{money(Number(row.gross))}</td>
                  <td>{money(Number(row.fee))}</td>
                  <td>{row.payout_reference || "—"}</td>
                  <td>
                    <span
                      class={`badge badge-sm ${row.status === "paid" ? "badge-success" : "badge-warning"}`}
                    >
                      {row.status === "paid"
                        ? t("reports.settlements.paid")
                        : t("reports.settlements.pending")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!tracker.isLoading.value && rows.value.length === 0 ? (
            <div class="p-8 text-center text-base-content/60">
              {t("reports.settlements.empty")}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
});
